/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
/** biome-ignore-all lint/style/useNamingConvention: allow snake case */

import cx from 'classnames';
import {type MouseEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {apiClient} from '~/api/api-client';
import {EVENT_STATUS_DRAFT} from '~/common/utils/event_constants';
import {trackUpsell} from '~/common/utils/upsellTracking';
import {subscribeToUserStateChanges} from '~/common/utils/userStateSync';
import {FilterManager} from '~/GalleryPB/components/FilterManager/FilterManager';
import {GalleryResults} from '~/GalleryPB/components/GalleryResults/GalleryResults';
import {Header} from '~/GalleryPB/components/Header';
import {NoFavorites} from '~/GalleryPB/components/NoFavorites/NoFavorites';
import {ResultsHeader} from '~/GalleryPB/components/ResultsHeader/ResultsHeader';
import {SEODescriptionBlock} from '~/GalleryPB/components/SEODescriptionBlock';
import {SeoHeaderWithBreadcrumb} from '~/GalleryPB/components/SeoHeaderWithBreadcrumb/SeoHeaderWithBreadcrumb';
import {SubCategories} from '~/GalleryPB/components/SubCategories/SubCategories';
import {QuickView} from '~/GalleryPB/components/Template/components/QuickView/QuickView';
import {useTemplateHelpers} from '~/GalleryPB/hooks/useTemplateHelpers';
import {selectFavorites} from '~/GalleryPB/reducers/favorites';
import {
  selectActiveFilters,
  selectSortBy,
  setActiveFilters,
  setSortBy,
} from '~/GalleryPB/reducers/filters';
import {
  resetFilters as resetFiltersDispatch,
  selectGalleryCategoryId,
  selectGalleryDataBreadcrumbs,
  selectGalleryDataCurrentPage,
  selectGalleryDataPageCount,
  selectGalleryDataPageDisplayName,
  selectGalleryDataPreviousCurrentPage,
  selectGalleryDataRelatives,
  selectGalleryDataTracking,
  selectHasFilters,
  selectIsGalleryDataReplaced,
  selectNumTemplates,
  selectTemplates,
  setPreviousCurrentPage,
  updateActiveFilters,
} from '~/GalleryPB/reducers/galleryData';
import {type ActiveFilter, SortByOption} from '~/GalleryPB/types/filters';
import type {Template, VariantType} from '~/GalleryPB/types/template';
import {buildHref, history, processPageAttributes, showSeoHeader} from '~/GalleryPB/utils';
import {Divider} from '~library/atoms/Divider';
import {useIsLargeAndUp, useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {Pagination} from '~library/molecules/Pagination';
import {LoginModalWrapper} from '~library/organisms/LoginModal/LoginModalWrapper';
import {userLoggedIn} from '~library/organisms/LoginModal/reducer/actions';
import type {GalleryData} from './types/gallery';

const INVITATION_TEMPLATES_PER_ROW = 3;
const CARD_TEMPLATES_PER_ROW = 4;

const scrollToTopOfGallery = (desktop = false) => {
  const galleryRootContent = document.getElementById('gallery-root-content');
  if (galleryRootContent) {
    const bounds = galleryRootContent.getBoundingClientRect();
    if (bounds.top >= 0 && bounds.top < window.innerHeight - 100) return; // Top of the gallery content is fully visible
  }
  const marketingBannerHeight =
    (document.querySelector('.marketing-banner') as HTMLElement | null)?.offsetHeight || 0;
  const globalNavHeight = document.getElementById('meganav-variant-global-nav')?.offsetHeight || 0;
  const seoHeaderWithBreadcrumbHeight =
    document.getElementById('seo-header-with-breadcrumb')?.offsetHeight || 0;
  const headerHeight = document.getElementById('gallery-header')?.offsetHeight || 0;
  const gallerySubcategoriesHeight =
    document.getElementById('gallery-subcategories')?.offsetHeight || 0;

  const totalOffset =
    marketingBannerHeight +
    globalNavHeight +
    seoHeaderWithBreadcrumbHeight +
    headerHeight +
    gallerySubcategoriesHeight +
    (desktop ? 2 : 0); // accounts for small jump on desktop due to borders/margins

  scrollTo({top: totalOffset, behavior: 'instant'});
};

export const GalleryRoot = () => {
  const dispatch = useDispatch();
  const isMediumAndUp = useIsMediumAndUp();
  const isLargeAndUp = useIsLargeAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [isWaitingForSearchResults, setIsWaitingForSearchResults] = useState(false);
  const [isFiltersDrawerOpenDesktop, setIsFiltersDrawerOpenDesktop] = useState(true); // Default to open on desktop
  const [isFiltersDrawerOpenMW, setIsFiltersDrawerOpenMW] = useState(false); // Default to closed on MW
  const pageDisplayName = useSelector(selectGalleryDataPageDisplayName);
  const activeFilters = useSelector(selectActiveFilters, shallowEqual);
  const [changedFilters, setChangedFilters] = useState<ActiveFilter[]>([]);
  const previousCurrentPage = useSelector(selectGalleryDataPreviousCurrentPage);
  const [hideQuickViewCtas, setHideQuickViewCtas] = useState(false);
  const [templateToQuickView, setTemplateToQuickView] = useState<Template | null>(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const {findTemplateById, checkIfQuickViewEnabled} = useTemplateHelpers();
  const sortBy = useSelector(selectSortBy);
  const initialSortBy = useRef(sortBy);

  useEffect(() => {
    if (isLargeAndUp) setIsFiltersDrawerOpenMW(false);
  }, [isLargeAndUp]);

  // Subscribe to user state changes from other stores
  useEffect(() => {
    return subscribeToUserStateChanges((userData) => {
      if (userData?.userId) {
        dispatch(
          userLoggedIn(
            userData.userId,
            userData.userName,
            userData.subscriptionPlanName,
            userData.email,
            userData.avatarDisk,
            userData.imgUrl,
            userData.initials,
            userData.firstName
          )
        );
      }
    });
  }, [dispatch]);

  const templates = useSelector(selectTemplates, shallowEqual);
  const tracking = useSelector(selectGalleryDataTracking);
  const categoryId = useSelector(selectGalleryCategoryId);
  const breadcrumbs = useSelector(selectGalleryDataBreadcrumbs, shallowEqual);
  const currentPage = useSelector(selectGalleryDataCurrentPage);
  const pageCount = useSelector(selectGalleryDataPageCount);
  const relatives = useSelector(selectGalleryDataRelatives, shallowEqual);
  const replaced = useSelector(selectIsGalleryDataReplaced);
  const numTemplates = useSelector(selectNumTemplates);
  const hasFilters = useSelector(selectHasFilters);

  const favorites = useSelector(selectFavorites, shallowEqual);

  const urlPieces = useRef(window.location.pathname.split('/')).current;

  const galleryCategoryBasePath = window.cards_page ? '/cards' : '/invites';

  document.documentElement.style.setProperty(
    '--gallery-templates-per-row-desktop',
    window.cards_page ? CARD_TEMPLATES_PER_ROW.toString() : INVITATION_TEMPLATES_PER_ROW.toString()
  );

  /**
   * Determines if a given pathname is a gallery category path.
   *
   * This function checks if the input pathname starts with '/invites' or '/cards',
   * implying that the path is for a gallery category.
   *
   * @param {string} pathname - The pathname to check.
   * @returns {boolean} Returns true if the pathname starts with '/invites' or '/cards', else returns false.
   */
  const isGalleryCategoryPathname = useCallback(
    (pathname: string) => pathname.startsWith('/invites') || pathname.startsWith('/cards'),
    []
  );

  /**
   * Pushes a new path to the history object, updating the URL and history stack.
   *
   * @param {URLSearchParams} searchParams - The URL search parameters.
   * @param {Object} activeFiltersToInit - The currently active filters for the data.
   *
   * Modifies the URL and history stack by pushing a new path derived from current pathname and base path.
   */
  const pushPathToHistory = useCallback(
    (searchParams: URLSearchParams, activeFiltersToInit: ActiveFilter[]) => {
      let {pathname} = window.location;
      if (isGalleryCategoryPathname(pathname)) {
        const hierarchicalCategoriesPath = window.location.pathname
          .split('/')
          .slice(2, -1)
          .join('/');
        pathname = `${galleryCategoryBasePath}/${
          hierarchicalCategoriesPath ? `${hierarchicalCategoriesPath}/` : ''
        }`;
      }
      history.push({
        pathname,
        hash: history.location.hash,
        search: searchParams.toString(),
        state: {
          activeFilters: activeFiltersToInit,
        },
      });
    },
    [galleryCategoryBasePath, isGalleryCategoryPathname]
  );

  /**
   * Submit filters and get new gallery data
   * @param filtersToSubmit
   * @param page
   * @returns {Promise<{
   *    breadcrumbs?: object[],
   *    categoryId?: string,
   *    displayName?: string,
   *    filterSections: object[],
   *    freeTemplates: object[],
   *    page: number,
   *    pageCount: number,
   *    premiumTemplates: object[],
   *    searchText?: string,
   *    total: number,
   *    cards_page?: boolean,
   *    category_name?: string,
   *    category_id?: string,
   *    query?: string,
   *    is_favorites?: boolean,
   *    seo_description_copy?: string,
   *    seo_display_name?: string,
   *    top_of_page_copy?: string,
   *    top_of_page_img_desk?: string,
   *    top_of_page_img_mob?: string,
   *    meta_title?: string,
   *    meta_description?: string,
   *    noindex_url?: boolean,
   * }>}
   */
  const refreshGalleryData = useCallback(
    async (
      filtersToSubmit: ActiveFilter[] = [],
      page: number = 1,
      sortBy: SortByOption = SortByOption.MOST_POPULAR
    ): Promise<GalleryData> => {
      scrollToTopOfGallery(isLargeAndUp);
      // Convert list of filters to submit into a filter object for the API request
      const filterData: Record<string, string[]> = {};
      filtersToSubmit.forEach((f) => {
        if (Object.keys(filterData).includes(f.filterSection)) {
          filterData[f.filterSection].push(f.filterName);
        } else {
          filterData[f.filterSection] = [f.filterName];
        }
      });

      let response: Response;
      let categoryID = window.category_id;
      if (categoryID) {
        const requestBody = {
          filters: Object.keys(filterData).length > 0 ? filterData : undefined,
          page,
          category_id: categoryID,
          match_to: params.get('match_to'),
          invitation_type: window.cards_page ? 'card' : 'invitation',
          sort_by: sortBy,
        };
        // Submit request and return response body
        response = await apiClient.post(`/ajax/gallery/query`, requestBody);
      } else {
        const searchChunk = urlPieces.find((p) => p === 'search');
        const searchParams = new URLSearchParams(window.location.search);
        const searchTerm = searchParams.get('query');
        if (searchChunk && searchTerm) {
          response = await apiClient.post(`/ajax/gallery/query`, {
            filters: Object.keys(filterData).length > 0 ? filterData : undefined,
            page,
            search_term: searchTerm,
            invitation_type: searchParams.get('filter'),
            sort_by: sortBy,
          });
        } else if (urlPieces.includes('favorites')) {
          categoryID = 'favorites';
          response = await apiClient.post(`/ajax/gallery/query`, {
            category_id: categoryID,
          });
        } else {
          // Fall back to showing featured items
          categoryID = window.cards_page ? 'featured_cards' : 'featured';
          const requestBody = {
            filters: Object.keys(filterData).length > 0 ? filterData : undefined,
            page,
            category_id: categoryID,
            invitation_type: window.cards_page ? 'card' : 'invitation',
            sort_by: sortBy,
          };
          response = await apiClient.post(`/ajax/gallery/query`, requestBody);
        }
      }
      const data: GalleryData = await response.json();
      const result: GalleryData = {...data};
      if (result.replaced) result.category_id = categoryID;
      processPageAttributes(result);

      return result;
    },
    [params, urlPieces, isLargeAndUp]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    // Get initial gallery items, filters, breadcrumbs, and page data
    const url = new URL(window.location.href);
    pushPathToHistory(url.searchParams, activeFilters);
  }, []);

  /**
   * Handle the user pressing Cancel
   * @returns {Promise<void>}
   */
  const handleCancelClick = useCallback(async () => {
    setIsFiltersDrawerOpenDesktop(false);
    setIsFiltersDrawerOpenMW(false);

    if (changedFilters.length === 0 && sortBy === initialSortBy.current) return;

    const revertedFilters = [...activeFilters];

    // Reset active filters to previous state
    changedFilters.forEach((cf) => {
      if (cf.on) {
        const idx = revertedFilters.findIndex((af) => af.filterName === cf.filterName);
        if (idx >= 0) revertedFilters.splice(idx, 1);
      } else {
        const idx = revertedFilters.findIndex((af) => af.filterName === cf.filterName);
        if (idx >= 0) revertedFilters[idx].on = true;
        else revertedFilters.push({...cf, on: true});
      }
    });

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('active_filter');

    revertedFilters.forEach((f) => {
      searchParams.append('active_filter', `${f.filterSection},${f.filterName}`);
    });

    // Request updated gallery data with previous filters and revert in Redux and state values
    if (previousCurrentPage) searchParams.set('page', previousCurrentPage.toString());
    pushPathToHistory(searchParams, revertedFilters);
    setIsWaitingForSearchResults(true);
    const apiResponse = await refreshGalleryData(
      revertedFilters,
      previousCurrentPage,
      initialSortBy.current
    );
    if (changedFilters.length === 0) {
      dispatch(updateActiveFilters({apiResponse}));
    } else {
      changedFilters.forEach((cf) => {
        dispatch(
          updateActiveFilters({
            sectionName: cf.filterSection,
            filterName: cf.filterName,
            apiResponse,
          })
        );
      });
    }

    setIsWaitingForSearchResults(false);
    setChangedFilters([]);
    dispatch(setActiveFilters(revertedFilters));
    dispatch(setSortBy(initialSortBy.current));
  }, [
    activeFilters,
    changedFilters,
    previousCurrentPage,
    pushPathToHistory,
    refreshGalleryData,
    dispatch,
    sortBy,
  ]);

  /**
   * Clear all filters
   */
  const handleClearFilters = useCallback(async () => {
    setIsWaitingForSearchResults(true);
    const apiResponse = await refreshGalleryData([], 1, sortBy);
    setIsWaitingForSearchResults(false);
    dispatch(resetFiltersDispatch({apiResponse}));

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('active_filter');
    if (searchParams.get('page'))
      // reset to page 1 if paginated
      searchParams.set('page', '1');
    pushPathToHistory(searchParams, []);

    dispatch(setActiveFilters([]));
    setChangedFilters([]);
    dispatch(setPreviousCurrentPage(1));

    return apiResponse;
  }, [pushPathToHistory, refreshGalleryData, dispatch, sortBy]);

  /**
   * Handle the user pressing Apply (this is a feel-good button)
   */
  const handleApplyClick = useCallback(
    (changed = changedFilters) => {
      // Update list of active filters
      const updatedActiveFilters = [...activeFilters];
      const activeFilterNames = updatedActiveFilters.map((af) => af.filterName);
      changed.forEach((cf) => {
        if (!activeFilterNames.includes(cf.filterName) && cf.on) {
          updatedActiveFilters.push(cf);
        } else if (!cf.on) {
          const idx = updatedActiveFilters.findIndex((af) => af.filterName === cf.filterName);
          if (idx >= 0) updatedActiveFilters.splice(idx, 1);
        }
      });
      dispatch(setActiveFilters(updatedActiveFilters));
      if (isLargeAndUp) setChangedFilters([]);
      else setIsFiltersDrawerOpenDesktop(false);
    },
    [activeFilters, changedFilters, dispatch, isLargeAndUp]
  );

  /**
   * Close the filter manager
   */
  const handleCloseFilterManager = useCallback(() => {
    setIsFiltersDrawerOpenMW(false);
    setIsFiltersDrawerOpenDesktop(false);
    setChangedFilters([]);
  }, []);

  /**
   * Handle the user toggling a filter
   * @param sectionName the filter section
   * @param filterId the name of the filter
   * @param on whether or not we're turning the filter on or off
   * @param forceApply whether or not to force-apply the filter change all the way through
   * @returns {Promise<void>}
   */
  const handleFilterClick = useCallback(
    async (sectionName: string, filterId: string, on: boolean) => {
      // Build list of filters to submit
      const filtersToSubmit: ActiveFilter[] = [...activeFilters];
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('active_filter');

      // Update list of changed filters
      const changed = [...changedFilters];
      const idx = changed.findIndex((f) => f.filterName === filterId);
      if (idx >= 0) changed.splice(idx, 1);
      else
        changed.push({
          filterSection: sectionName,
          filterName: filterId,
          on,
        });

      const filterInActiveList = filtersToSubmit.findIndex((f) => f.filterName === filterId);
      if (on && filterInActiveList < 0) {
        // Filter is newly on; add it to the list of filters to submit
        filtersToSubmit.push({
          filterSection: sectionName,
          filterName: filterId,
          on: true,
        });
      } else if (!on && filterInActiveList >= 0) {
        // Filter is being turned off; remove it from the list of filters to submit
        filtersToSubmit.splice(filterInActiveList, 1);
      }

      filtersToSubmit.forEach((f) => {
        searchParams.append('active_filter', `${f.filterSection},${f.filterName}`);
      });

      if (searchParams.get('page'))
        // reset to page 1 if paginated
        searchParams.set('page', '1');
      pushPathToHistory(searchParams, filtersToSubmit);

      // Get updated gallery data and update in Redux and state variables
      setIsWaitingForSearchResults(true);
      const apiResponse = await refreshGalleryData(filtersToSubmit, 1, sortBy);
      dispatch(
        updateActiveFilters({
          sectionName,
          filterName: filterId,
          apiResponse,
        })
      );
      setIsWaitingForSearchResults(false);

      if (!isLargeAndUp) {
        dispatch(setActiveFilters(filtersToSubmit));
        setChangedFilters(changed);
      } else handleApplyClick(changed);
    },
    [
      activeFilters,
      changedFilters,
      handleApplyClick,
      isLargeAndUp,
      pushPathToHistory,
      refreshGalleryData,
      dispatch,
      sortBy,
    ]
  );

  const onSortByChange = useCallback(
    async (sortBy: SortByOption) => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', sortBy);
      pushPathToHistory(searchParams, activeFilters);
      setIsWaitingForSearchResults(true);
      const apiResponse = await refreshGalleryData(activeFilters, 1, sortBy);
      setIsWaitingForSearchResults(false);
      dispatch(updateActiveFilters({apiResponse}));
      dispatch(setSortBy(sortBy));
      dispatch(setPreviousCurrentPage(1));
    },
    [activeFilters, pushPathToHistory, refreshGalleryData, dispatch]
  );

  /**
   * Handle the user pressing the Filters button to open the filter drawer
   */
  const onFilterButtonClick = useCallback(() => {
    if (isLargeAndUp) setIsFiltersDrawerOpenDesktop((prev) => !prev);
    else setIsFiltersDrawerOpenMW((prev) => !prev);
  }, [isLargeAndUp]);

  /**
   * Handle the user clicking on a pagination page
   * @param page the page to take the user to
   */
  const onPaginationPagePress = useCallback(
    async (page: number) => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('page', page.toString());
      pushPathToHistory(searchParams, activeFilters);
      setIsWaitingForSearchResults(true);
      const apiResponse = await refreshGalleryData(activeFilters, page, sortBy);
      setIsWaitingForSearchResults(false);
      dispatch(updateActiveFilters({apiResponse}));

      dispatch(setPreviousCurrentPage(1));
    },
    [activeFilters, pushPathToHistory, refreshGalleryData, dispatch, sortBy]
  );

  /**
   * Handle the user clicking the left pagination arrow
   */
  const onPaginationLeftArrowPress = useCallback(() => {
    // Note: if we hit an edge case where the previous page would be 0, keep the user at 1
    const page = Math.max(currentPage - 1, 1);
    return onPaginationPagePress(page);
  }, [currentPage, onPaginationPagePress]);

  /**
   * Handle the user clicking the right pagination arrow
   */
  const onPaginationRightArrowPress = useCallback(() => {
    // Note: if we hit an edge case where the next page would be beyond the page count, keep the user at the last page
    const page = Math.min(currentPage + 1, pageCount);
    return onPaginationPagePress(page);
  }, [currentPage, onPaginationPagePress, pageCount]);

  const onClickQuickViewOpenHandler = useCallback(
    (templateId: string) => {
      const template = findTemplateById(templateId);
      setTemplateToQuickView(template ?? null);
      setQuickViewOpen(true);
    },
    [findTemplateById]
  );

  const onClickQuickViewCloseHandler = useCallback(() => {
    setQuickViewOpen(false);
    setHideQuickViewCtas(false);
  }, []);

  const onTemplateClick = useCallback(
    (e: MouseEvent, templateId: string, href: string) => {
      e.preventDefault();
      e.stopPropagation();

      // Find template
      const template = findTemplateById(templateId);
      if (!template) return;
      const isCard = template.usedAs === 'card';
      const isQuickViewEnabled = checkIfQuickViewEnabled(template);

      if (
        !isCard &&
        isQuickViewEnabled &&
        ((!template.premium && isMediumAndUp) || !isMediumAndUp)
      ) {
        setTemplateToQuickView(template);
        setQuickViewOpen(true);
        return;
      }

      window.location.href = href;
    },
    [findTemplateById, checkIfQuickViewEnabled, isMediumAndUp]
  );

  const onStartCustomizingClick = useCallback(
    (_: MouseEvent<HTMLButtonElement>, variant: VariantType) => {
      if (!templateToQuickView) return;
      const href = buildHref(templateToQuickView, tracking, currentPage);
      const url = new window.URL(`${window.location.origin}${href ?? ''}`);
      url.searchParams.append('variant', variant);
      if (variant === 'premium') {
        trackUpsell({
          path: window.location.pathname.split('/').at(0) || 'invites',
          feature: 'design',
          status: EVENT_STATUS_DRAFT,
        });
      }
      window.location.href = url.toString();
    },
    [currentPage, tracking, templateToQuickView]
  );

  if (window.is_favorites_page && (!favorites || favorites.length === 0)) {
    return favorites ? <NoFavorites /> : null;
  }
  // Don't render if results haven't come in yet to reduce CLS score
  if (!favorites?.length && !templates?.free?.length && !templates?.premium?.length) return null;

  return (
    <>
      <SeoHeaderWithBreadcrumb />

      <div
        className={cx('gallery__root__content', {
          'gallery__root__content__with-seo-header': showSeoHeader,
        })}
        id="gallery-root-content"
      >
        <Header
          crumbPath={breadcrumbs}
          pageTitle={pageDisplayName}
          numTemplates={numTemplates}
          relatedCategories={relatives}
        />
        {!isLargishAndUp && !urlPieces.includes('favorites') ? (
          <SubCategories group={relatives} />
        ) : null}
        <ResultsHeader
          onFilterButtonClick={onFilterButtonClick}
          onSortByChange={onSortByChange}
          numTemplates={numTemplates}
          showFilterButton={hasFilters}
          areFiltersOpen={isLargishAndUp ? isFiltersDrawerOpenDesktop : isFiltersDrawerOpenMW}
        />
        {isLargishAndUp ? (
          <div
            className={`gallery-body__desktop ${urlPieces.includes('favorites') ? 'favorites-page' : ''}`}
          >
            <FilterManager
              open={hasFilters && isFiltersDrawerOpenDesktop}
              onFilterClick={handleFilterClick}
              onSortByChange={onSortByChange}
              onCancelChanges={handleCancelClick}
              onApplyFilters={handleApplyClick}
              onClearFilters={handleClearFilters}
            />
            <div className="gallery-body__desktop__gallery-results-container">
              <GalleryResults
                freeTemplates={templates.free || []}
                premiumTemplates={templates.premium || []}
                favorites={favorites || []}
                showNoResultsHeader={replaced} // Controls if No Search Results header is displayed
                categoryId={categoryId}
                onTemplateClick={onTemplateClick}
                onClickQuickView={onClickQuickViewOpenHandler}
                onClearFilters={handleClearFilters}
                isLoading={isWaitingForSearchResults}
              />
              {pageCount > 1 && (
                <>
                  <Divider marginTop="2.5rem" marginBottom="2.5rem" />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pageCount}
                    onPagePress={onPaginationPagePress}
                    onLeftArrowPress={onPaginationLeftArrowPress}
                    onRightArrowPress={onPaginationRightArrowPress}
                  />
                  <Divider marginTop="2rem" marginBottom="2rem" />
                </>
              )}
              <SEODescriptionBlock />
            </div>
          </div>
        ) : (
          <>
            <div className="gallery-body__mw">
              {isLargeAndUp && (
                <FilterManager
                  open={hasFilters && isFiltersDrawerOpenDesktop}
                  onFilterClick={handleFilterClick}
                  onSortByChange={onSortByChange}
                  onCancelChanges={handleCancelClick}
                  onApplyFilters={handleApplyClick}
                  onClearFilters={handleClearFilters}
                />
              )}
              <GalleryResults
                freeTemplates={templates.free || []}
                premiumTemplates={templates.premium || []}
                favorites={favorites || []}
                showNoResultsHeader={replaced} // Controls if No Search Results header is displayed
                categoryId={categoryId}
                onTemplateClick={onTemplateClick}
                onClickQuickView={onClickQuickViewOpenHandler}
                isLoading={isWaitingForSearchResults}
                onClearFilters={handleClearFilters}
              />
            </div>
            {pageCount > 1 && (
              <>
                <Divider marginTop="2rem" marginBottom="2rem" />
                <Pagination
                  currentPage={currentPage}
                  totalPages={pageCount}
                  onPagePress={onPaginationPagePress}
                  onLeftArrowPress={onPaginationLeftArrowPress}
                  onRightArrowPress={onPaginationRightArrowPress}
                />
              </>
            )}
            {!isLargeAndUp && (
              <FilterManager
                open={hasFilters && isFiltersDrawerOpenMW}
                onFilterClick={handleFilterClick}
                onSortByChange={onSortByChange}
                onCancelChanges={handleCancelClick}
                onApplyFilters={handleCloseFilterManager}
                onClearFilters={handleClearFilters}
                isLoading={isWaitingForSearchResults}
              />
            )}
            {pageCount > 1 && <Divider marginTop="2rem" marginBottom="2rem" />}
            <SEODescriptionBlock />
          </>
        )}
        {quickViewOpen && templateToQuickView && (
          <QuickView
            key={quickViewOpen.toString()}
            open={quickViewOpen}
            onClose={onClickQuickViewCloseHandler}
            template={templateToQuickView}
            onStartCustomizingClick={onStartCustomizingClick}
            openQuickView={onClickQuickViewOpenHandler}
            hideCtas={hideQuickViewCtas}
          />
        )}
        {createPortal(<LoginModalWrapper />, document.body)}
      </div>
    </>
  );
};
